import Chart from 'chart.js/auto';
import { App } from '../admin.js';
import { MonedaService } from '../services/moneda-service.js';
import { escapeHtmlParaInyeccionSegura } from '../utils/escape-html-para-inyeccion-segura.js';
import { DesgloseMonedaPopover } from './desglose-moneda-popover.js';
import { t } from '../services/i18n-admin.js';

/**
 * NodeStatisticsDashboard — dashboard pro de estadísticas por nodo.
 *
 * Reemplazo de StatsModal (texto plano) con KPIs visuales, gráficos
 * Chart.js y panel de contrato/obligaciones/cortes. Recursivo: agrega
 * stats de todo el sub-árbol del nodo.
 *
 * Mapa de secciones (orden del archivo):
 *    1. ESTADO                memoria mutable (nodoId, charts, datos).
 *    2. API PÚBLICA           open() / close() — entrypoints.
 *    3. PERÍODO               selector de rango (hoy/semana/mes/custom).
 *    4. DATA FETCHING         fetches al backend agrupados.
 *    5. RENDERING             pinta cards KPI + paneles secundarios.
 *    6. CHARTS                builders de datasets Chart.js + render.
 *    7. CHART HELPERS         _addChart / _destroyCharts / _createChartSlot
 *                             (mismo patrón que LotowheelSimulator).
 *    8. CONTRATO Y OBLIGACIONES  panel inline con info contractual del nodo.
 *    9. HISTORIAL DE CORTES   tabla colapsable de cortes recientes.
 *   10. EXPORTACIÓN           texto plano, imagen PNG, compartir WhatsApp.
 *
 * Charts dependen de Chart.js. Patrón _add/_destroy/_createSlot replicado
 * de LotowheelSimulator para consistencia entre dashboards.
 */
export const NodeStatisticsDashboard = {
    // ═══════════════════════════════════════════════════════════════════════
    // ESTADO — memoria mutable (nodoId, charts vivos, datos cacheados).
    // ═══════════════════════════════════════════════════════════════════════
    _nodoId: null,
    _nodoNombre: null,
    _nodoTipo: null,
    _currentPeriod: 'mes',
    _rangoDesde: null,
    _rangoHasta: null,

    // Datos cacheados de la última carga
    _lastStatsResponse: null,
    _lastEvolutionResponse: null,
    _lastContratoResponse: null,
    _lastObligacionesResponse: null,

    // Chart.js lifecycle
    _activeCharts: [],

    // Colores consistentes con el dark theme
    CHART_COLOR_VENTAS: '#22c55e',
    CHART_COLOR_PREMIOS: '#3b82f6',
    CHART_COLOR_NETWIN: '#f59e0b',
    CHART_COLOR_DEPOSITOS: '#06b6d4',
    CHART_COLOR_RETIROS: '#ef4444',
    CHART_COLOR_MARGEN: '#a855f7',

    // ═══════════════════════════════════════════════════════════════════════
    // API PÚBLICA — open(nodoId) / close() — entrypoints del dashboard.
    // ═══════════════════════════════════════════════════════════════════════

    async open(nodoId, nodoNombre, nodoTipo) {
        this._nodoId = nodoId;
        this._nodoNombre = nodoNombre;
        this._nodoTipo = nodoTipo;
        this._currentPeriod = 'mes';

        // Configurar título
        const titleEl = document.getElementById('nodeStatsDashboardTitle');
        if (!titleEl) {
            console.error('[StatsDashboard] Modal no inyectado — #nodeStatsDashboardTitle no encontrado');
            return;
        }
        titleEl.textContent = t('Estadísticas: ') + nodoNombre;

        // Resetear UI
        this._resetPeriodButtons('mes');
        document.getElementById('nodeStatsCustomRangeInputs').style.display = 'none';

        App.openModal('modalNodeStatsDashboard');

        // Calcular rango y cargar datos
        const range = this._calculateDateRange('mes');
        this._rangoDesde = range.desde;
        this._rangoHasta = range.hasta;

        await this._loadAndRenderDashboard();
    },

    close() {
        this._destroyCharts();
        App.closeModal();
    },

    // ═══════════════════════════════════════════════════════════════════════
    // PERÍODO — selector de rango (hoy/semana/mes/custom) + datepickers.
    // ═══════════════════════════════════════════════════════════════════════

    async setPeriod(period) {
        if (period === 'rango') {
            this.toggleCustomRangeInputs();
            return;
        }

        this._currentPeriod = period;
        this._resetPeriodButtons(period);
        document.getElementById('nodeStatsCustomRangeInputs').style.display = 'none';

        const range = this._calculateDateRange(period);
        this._rangoDesde = range.desde;
        this._rangoHasta = range.hasta;

        await this._loadAndRenderDashboard();
    },

    toggleCustomRangeInputs() {
        const rangoInputs = document.getElementById('nodeStatsCustomRangeInputs');
        const isVisible = rangoInputs.style.display !== 'none';

        if (isVisible) {
            rangoInputs.style.display = 'none';
        } else {
            rangoInputs.style.display = 'block';
            const desdeInput = document.getElementById('nodeStatsDesde');
            const hastaInput = document.getElementById('nodeStatsHasta');
            if (!desdeInput.value) {
                const today = new Date().toISOString().split('T')[0];
                desdeInput.value = today;
                hastaInput.value = today;
            }
        }
    },

    async applyCustomRange() {
        const desde = document.getElementById('nodeStatsDesde').value;
        const hasta = document.getElementById('nodeStatsHasta').value;

        if (!desde || !hasta) {
            App.toast(t('Selecciona ambas fechas'), 'error');
            return;
        }
        if (desde > hasta) {
            App.toast(t('La fecha "desde" debe ser anterior a "hasta"'), 'error');
            return;
        }

        this._currentPeriod = 'rango';
        this._rangoDesde = desde;
        this._rangoHasta = hasta;

        this._resetPeriodButtons('rango');

        await this._loadAndRenderDashboard();
    },

    _calculateDateRange(period) {
        const today = new Date();
        const formatDate = d => d.toISOString().split('T')[0];

        switch (period) {
            case 'hoy':
                return { desde: formatDate(today), hasta: formatDate(today) };

            case 'ayer': {
                const ayer = new Date(today);
                ayer.setDate(ayer.getDate() - 1);
                return { desde: formatDate(ayer), hasta: formatDate(ayer) };
            }

            case 'semana': {
                const hace7Dias = new Date(today);
                hace7Dias.setDate(today.getDate() - 7);
                return { desde: formatDate(hace7Dias), hasta: formatDate(today) };
            }

            case 'mes': {
                const hace30Dias = new Date(today);
                hace30Dias.setDate(today.getDate() - 30);
                return { desde: formatDate(hace30Dias), hasta: formatDate(today) };
            }

            case 'ultimo_corte':
                return { desde: null, hasta: null };

            default:
                return { desde: formatDate(today), hasta: formatDate(today) };
        }
    },

    _resetPeriodButtons(activePeriod) {
        const container = document.getElementById('modalNodeStatsDashboard');
        if (!container) return;
        const buttons = container.querySelectorAll('.stats-date-btn');
        for (let i = 0; i < buttons.length; i++) {
            buttons[i].classList.remove('active');
            if (buttons[i].getAttribute('data-period') === activePeriod) {
                buttons[i].classList.add('active');
            }
        }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // DATA FETCHING — fetches al backend (stats, contrato, cortes).
    // ═══════════════════════════════════════════════════════════════════════

    async _loadAndRenderDashboard() {
        const loadingEl = document.getElementById('nodeStatsDashboardLoading');
        const contentEl = document.getElementById('nodeStatsDashboardContent');

        loadingEl.style.display = 'flex';
        contentEl.style.display = 'none';
        const errorBanner = document.getElementById('nodeStatsDashboardErrorBanner');
        if (errorBanner) errorBanner.style.display = 'none'; // limpiar error de un intento previo
        this._destroyCharts();
        this._updateRangeLabel();

        try {
            const desdeParam = this._rangoDesde || '';
            const hastaParam = this._rangoHasta || '';

            // 4 fetches en paralelo
            const [
                statsResponse,
                evolutionResponse,
                contratoResponse,
                obligacionesResponse,
                jugadoresResponse,
                ticketsResponse,
            ] = await Promise.all([
                App.fetchWithAuth(
                    '/api/admin/nodos/' + this._nodoId + '/stats?desde=' + desdeParam + '&hasta=' + hastaParam
                ),
                App.fetchWithAuth(
                    '/api/admin/nodos/' +
                        this._nodoId +
                        '/stats/evolution?desde=' +
                        (this._rangoDesde || '') +
                        '&hasta=' +
                        (this._rangoHasta || '')
                ),
                App.fetchWithAuth('/api/admin/nodos/' + this._nodoId + '/contrato').catch(() => null),
                App.fetchWithAuth('/api/admin/obligaciones/' + this._nodoId + '/efectivas').catch(() => null),
                App.fetchWithAuth(
                    '/api/admin/nodos/' + this._nodoId + '/jugadores?desde=' + desdeParam + '&hasta=' + hastaParam
                ).catch(() => null),
                App.fetchWithAuth(
                    '/api/admin/nodos/' + this._nodoId + '/tickets?desde=' + desdeParam + '&hasta=' + hastaParam
                ).catch(() => null),
            ]);

            this._lastStatsResponse = statsResponse?.stats || null;
            this._lastEvolutionResponse = evolutionResponse || null;
            this._lastContratoResponse = contratoResponse?.contrato || null;
            this._lastObligacionesResponse = obligacionesResponse?.obligaciones || null;
            this._lastJugadoresResponse = jugadoresResponse || null;
            this._lastTicketsResponse = ticketsResponse || null;
            this._ticketsCargados = false; // se renderiza al abrir el tab Tickets (lazy)

            // Moneda del nodo (herencia fractal) — MonedaService formatea todo automaticamente
            MonedaService.setContexto(statsResponse?.stats?.monedaResuelta || MonedaService.DEFAULT);

            this._renderDashboard();
        } catch (err) {
            console.error('[NodeStatsDashboard] Error cargando datos:', err);
            // El error va a un banner propio, NO reemplaza el innerHTML de contentEl: hacerlo borraba
            // nodeStatsDashboardKpiContainer y los siguientes renders (aplicar rango) fallaban con
            // "Cannot set innerHTML of null" en cadena, envenenando el modal. Banner + estructura intacta.
            const banner = document.getElementById('nodeStatsDashboardErrorBanner');
            if (banner) {
                banner.textContent = t('Error al cargar estadísticas');
                banner.style.display = 'block';
            }
            contentEl.style.display = 'block';
        } finally {
            loadingEl.style.display = 'none';
        }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // RENDERING — pinta cards KPI (producción/financiero) + paneles.
    // ═══════════════════════════════════════════════════════════════════════

    _renderDashboard() {
        const contentEl = document.getElementById('nodeStatsDashboardContent');
        contentEl.style.display = 'block';

        const stats = this._lastStatsResponse;
        if (!stats) {
            contentEl.innerHTML =
                '<div class="node-stats-no-data">' + t('Sin datos para el período seleccionado') + '</div>';
            return;
        }

        // 1. KPIs
        this._renderKpiCards(stats);

        // 2. Gráficos (solo si hay datos de evolución con más de 1 punto)
        const evolution = this._lastEvolutionResponse?.evolution || [];
        this._renderCharts(evolution);

        // 3. Contrato y obligaciones
        this._renderContractInfo();

        // 4. Historial de cortes (si tiene último corte)
        this._renderCortesHistory(stats);

        // 5. Analytics de jugadores (KPIs + ranking + jugadas por hora)
        this._renderJugadores(this._lastJugadoresResponse);
    },

    _renderKpiCards(stats) {
        const container = document.getElementById('nodeStatsDashboardKpiContainer');
        if (!container) {
            console.error('[NodeStats] nodeStatsDashboardKpiContainer ausente — se omite el render de KPIs');
            return;
        }
        const produccion = stats.produccion || {};
        const financiero = stats.financiero || {};

        // FAIL-FAST en frontera (CLAUDE.md §2.A): el shape de /stats es un contrato.
        // Si el endpoint no trae jugado_total/pagado_total, NO enmascaramos con R$0
        // (eso ocultó por meses el bug de nombres ventas/premios vs jugado_total).
        // Gritamos en consola para que un mismatch de contrato sea VISIBLE, no un
        // cero mentiroso. Las tarjetas igual se pintan (degradar, no romper la UI),
        // pero el error queda registrado.
        // El guard compara con `== null` (cubre undefined Y null): con `=== undefined`
        // el null del consolidado multi-moneda pasaba silencioso, justo el caso nuevo.
        if (produccion.jugado_total === undefined || produccion.pagado_total === undefined) {
            console.error(
                '[NodeStats] Contrato roto: /stats no devolvió produccion.jugado_total/pagado_total.',
                'Shape recibido:',
                produccion
            );
        }

        // null = el backend NO pudo consolidar (red multi-moneda sin tasas usables):
        // no existe un total verdadero que mostrar. Se pinta un hueco con aviso, nunca
        // un 0 — un cero se lee como "no hubo actividad", más creíble y más falso.
        // Mismo criterio que salud-red-nodo.js:285.
        // Producción y financiero se miden con queries DISTINTAS y pueden diferir:
        // una puede venir convertida por tasa y la otra ser exacta. Cada bloque lee
        // su propio flag — usar uno solo marcaría de aproximado un total exacto, o
        // peor, presentaría como exacto uno que no lo es. `moneda_meta_produccion`
        // cae a `moneda_meta` para backends anteriores a este cambio.
        const meta = stats.moneda_meta || {};
        const metaProd = stats.moneda_meta_produccion || stats.moneda_meta || {};
        const sinConsolidar = valor => valor === null;
        const jugado = produccion.jugado_total;
        const ganado = produccion.pagado_total;
        const netwin = produccion.ggr;
        // RTP y Hold son RATIOS de dos cifras sin consolidar: no se pueden calcular.
        // Pintar "0.0%" en verde afirma que el hold es cero, que es una mentira
        // contable — distinto de "no lo sé".
        const hayRatios = !sinConsolidar(jugado) && !sinConsolidar(ganado) && jugado > 0;
        const rtpPorcentaje = hayRatios ? ((ganado / jugado) * 100).toFixed(1) : null;
        const holdPorcentaje = hayRatios ? (((jugado - ganado) / jugado) * 100).toFixed(1) : null;
        const saldoCC = stats.saldo_cuenta_corriente || 0;

        const netwinColorClass = sinConsolidar(netwin) ? 'gray' : netwin >= 0 ? 'green' : 'red';
        const rtpColorClass = rtpPorcentaje === null ? 'gray' : parseFloat(rtpPorcentaje) > 100 ? 'red' : 'green';
        const holdColorClass = holdPorcentaje === null ? 'gray' : parseFloat(holdPorcentaje) >= 0 ? 'green' : 'red';
        const saldoCCColorClass = saldoCC >= 0 ? 'purple' : 'red';

        // Aviso de total no confiable: mismo símbolo que salud-red-nodo (! ámbar).
        // El hueco tiene dos causas y el tooltip debe decir CUÁL: faltan tasas
        // (se resuelve cargándolas) o hay nodos sin moneda configurada (se resuelve
        // en la ficha del nodo). Un aviso genérico manda a buscar donde no es.
        const hayNodoSinMonedaEnAlgunBloque = Boolean(meta.nodos_sin_moneda || metaProd.nodos_sin_moneda);
        const motivoDelHueco = hayNodoSinMonedaEnAlgunBloque
            ? t('Há nós sem moeda configurada — o valor deles não pôde ser convertido')
            : t('Rede com moedas mistas e sem taxas de câmbio carregadas — total não confiável');
        const avisoSinTasa = `<span title="${motivoDelHueco}" style="color:#f59e0b;font-size:12px;">!</span>`;
        // Un formateador POR BLOQUE: el ≈ debe reflejar si ESE total se convirtió por
        // tasa, no el del bloque vecino. Producción puede venir consolidada (tickets
        // en tres monedas) mientras financiero es exacto, y al revés.
        // `campo` opcional: con él la marca abre el desglose por moneda; los totales
        // derivados en el front (efectivo = total − pasarela) no tienen clave propia.
        const fmtMontoCon =
            flags =>
            (c, campo) => {
                const hueco = sinConsolidar(c);
                const marca = campo ? DesgloseMonedaPopover.marcaHtml(flags, campo, { hueco }) : '';
                if (hueco) return marca || '—' + avisoSinTasa;
                if (c === 0) return '—';
                if (marca) return marca + ' ' + escapeHtmlParaInyeccionSegura(App.formatMoney(c));
                return (flags.consolidado_aproximado ? '≈ ' : '') + App.formatMoney(c);
            };
        const fmtMontoProduccion = fmtMontoCon(metaProd);
        const fmtMontoFinanciero = fmtMontoCon(meta);
        // Saldo CC no pasa por el consolidador (viene de getSaldoCuentaCorriente, un
        // escalar del nodo): nunca lleva ≈ ni puede ser null por monedas mixtas.
        const fmtMontoSaldoCC = c => (c === 0 ? '—' : App.formatMoney(c));
        const fmtPorcentaje = p => (p === null ? '—' + avisoSinTasa : p + '%');
        // Saldo CC es ACUMULADO del nodo, no del período: al filtrar un rango se rotula para que no
        // se lea como métrica de ese rango (el testeador vio "Saldo CC con datos" en un período vacío).
        const enRango = this._currentPeriod === 'rango';
        const saldoCCLabel = enRango ? t('Saldo CC') + ' *' : t('Saldo CC');

        container.innerHTML =
            this._buildKpiCard(t('Jugado'), fmtMontoProduccion(jugado, 'jugado_total'), 'green') +
            this._buildKpiCard(t('Ganado'), fmtMontoProduccion(ganado, 'pagado_total'), 'blue') +
            this._buildKpiCard('GGR', fmtMontoProduccion(netwin), netwinColorClass) +
            this._buildKpiCard('RTP', fmtPorcentaje(rtpPorcentaje), rtpColorClass) +
            this._buildKpiCard('Hold', fmtPorcentaje(holdPorcentaje), holdColorClass) +
            this._buildKpisDineroEntraSale(financiero, stats.nodo_tiene_pasarela, fmtMontoFinanciero) +
            this._buildKpiCard(saldoCCLabel, fmtMontoSaldoCC(saldoCC), saldoCCColorClass) +
            (enRango
                ? '<div class="node-stats-nota" style="grid-column:1/-1;font-size:11px;color:var(--text-secondary);margin-top:4px;">* ' +
                  t('Saldo CC es acumulado del nodo, no del período seleccionado') +
                  '</div>'
                : '');
    },

    // Depósitos (dinero que entra) y Retiros (dinero que sale), ADAPTATIVO: si el
    // nodo opera con pasarela (PIX/Yape), se separan en Pasarela vs Efectivo; si no
    // (ej. Perú sin pasarela), una sola tarjeta total de cada uno. Efectivo = total
    // − pasarela (identidad del modelo: recarga_dinero_fresco = efectivo + pasarela).
    _buildKpisDineroEntraSale(financiero, tienePasarela, fmtMonto) {
        // Sin consolidado los campos llegan null: se propaga a las tarjetas (fmtMonto
        // pinta el hueco). El efectivo es DERIVADO (total − pasarela), así que también
        // hereda el null: `null - null` daría 0 y mostraría un efectivo inventado.
        const depTotal = financiero.recarga_dinero_fresco;
        const retTotal = financiero.retiro_dinero_real;
        if (!tienePasarela) {
            return (
                this._buildKpiCard(t('Depósitos'), fmtMonto(depTotal, 'recarga_dinero_fresco'), 'cyan') +
                this._buildKpiCard(t('Retiros'), fmtMonto(retTotal, 'retiro_dinero_real'), 'orange')
            );
        }
        const depPix = financiero.recarga_dinero_pasarela;
        const retPix = financiero.retiro_dinero_pasarela;
        const restaONull = (total, parte) => (total === null || parte === null ? null : Math.max(0, total - parte));
        const depCash = restaONull(depTotal, depPix);
        const retCash = restaONull(retTotal, retPix);
        return (
            this._buildKpiCard(t('Dep. Pasarela'), fmtMonto(depPix, 'recarga_dinero_pasarela'), 'cyan') +
            this._buildKpiCard(t('Dep. Efectivo'), fmtMonto(depCash), 'cyan') +
            this._buildKpiCard(t('Ret. Pasarela'), fmtMonto(retPix, 'retiro_dinero_pasarela'), 'orange') +
            this._buildKpiCard(t('Ret. Efectivo'), fmtMonto(retCash), 'orange')
        );
    },

    _buildKpiCard(label, value, colorClass) {
        return (
            '<div class="node-stats-kpi-card">' +
            '<div class="node-stats-kpi-label">' +
            label +
            '</div>' +
            '<div class="node-stats-kpi-value ' +
            colorClass +
            '">' +
            value +
            '</div>' +
            '</div>'
        );
    },

    // ═══════════════════════════════════════════════════════════════════════
    // CHARTS — builders de datasets Chart.js + render por tipo de gráfico.
    // ═══════════════════════════════════════════════════════════════════════

    _renderCharts(evolution) {
        const container = document.getElementById('nodeStatsDashboardChartsContainer');
        container.innerHTML = '';

        if (evolution.length < 2) {
            // Con 0 o 1 punto (Hoy/Ayer) no hay evolución que graficar
            container.innerHTML =
                '<div class="node-stats-no-data" style="padding:20px 0;">' +
                t('Los gráficos de evolución requieren al menos 2 días (usa Semana, Mes o Rango)') +
                '</div>';
            return;
        }

        const labels = [];
        const ventasData = [];
        const premiosData = [];
        const netwinData = [];
        const depositosData = [];
        const retirosData = [];
        const margenData = [];

        for (let i = 0; i < evolution.length; i++) {
            const punto = evolution[i];
            labels.push(this._formatChartDateLabel(punto.fecha));
            // El endpoint /stats/evolution devuelve jugado_total / pagado_total
            // (nomenclatura contable del modelo), NO ventas/premios. El gráfico leía
            // punto.ventas/.premios → undefined → líneas verde/azul planas en 0.
            // Unidad menor → unidades display (factor real: 100 BRL/PEN, 1 PYG/COP).
            const F = MonedaService.getUnidadesMenoresPorUnidad(MonedaService.getContexto());
            // null = el backend no pudo consolidar ese punto (monedas mixtas sin tasas).
            // Se propaga como null, que Chart.js dibuja como HUECO en la línea. Dividir
            // directo lo colapsaría a 0 (`null / 100 === 0`) y pintaría un punto plano
            // indistinguible de un día sin juego: un gráfico creíble y mentiroso.
            const aDisplay = valor => (valor === null || valor === undefined ? null : valor / F);
            const ventasDelDia = aDisplay(punto.jugado_total);
            ventasData.push(ventasDelDia);
            premiosData.push(aDisplay(punto.pagado_total));
            netwinData.push(aDisplay(punto.ggr));
            depositosData.push(aDisplay(punto.recarga_dinero_fresco));
            retirosData.push(aDisplay(punto.retiro_dinero_real));
            const ggrDelDia = aDisplay(punto.ggr);
            // El margen es un RATIO: sin sus dos insumos no existe, no vale 0.
            margenData.push(
                ventasDelDia === null || ggrDelDia === null || !(ventasDelDia > 0)
                    ? null
                    : (ggrDelDia / ventasDelDia) * 100
            );
        }

        // Gráfico 1: Evolución de Producción (line)
        this._renderProductionChart(container, labels, ventasData, premiosData, netwinData);

        // Gráfico 2: Evolución Financiera (bar)
        this._renderFinancialChart(container, labels, depositosData, retirosData);

        // Gráfico 3: Margen % (line)
        this._renderMarginChart(container, labels, margenData);
    },

    _renderProductionChart(container, labels, ventasData, premiosData, netwinData) {
        const section = document.createElement('div');
        section.className = 'node-stats-chart-section';

        const title = document.createElement('div');
        title.className = 'node-stats-chart-title';
        title.textContent = t('Evolución de Producción');
        section.appendChild(title);

        const wrapper = document.createElement('div');
        wrapper.className = 'node-stats-chart-wrapper';
        wrapper.style.height = '280px';
        const canvas = document.createElement('canvas');
        wrapper.appendChild(canvas);
        section.appendChild(wrapper);
        container.appendChild(section);

        this._addChart(
            canvas.getContext('2d'),
            'line',
            labels,
            [
                {
                    label: t('Jugado'),
                    data: ventasData,
                    borderColor: this.CHART_COLOR_VENTAS,
                    backgroundColor: 'transparent',
                    tension: 0.3,
                    pointRadius: 2,
                    borderWidth: 2,
                },
                {
                    label: t('Ganado'),
                    data: premiosData,
                    borderColor: this.CHART_COLOR_PREMIOS,
                    backgroundColor: 'transparent',
                    tension: 0.3,
                    pointRadius: 2,
                    borderWidth: 2,
                },
                {
                    label: 'GGR',
                    data: netwinData,
                    borderColor: this.CHART_COLOR_NETWIN,
                    backgroundColor: 'transparent',
                    tension: 0.3,
                    pointRadius: 2,
                    borderWidth: 2,
                },
            ],
            this._buildDarkThemeMoneyChartOptions()
        );
    },

    _renderFinancialChart(container, labels, depositosData, retirosData) {
        const section = document.createElement('div');
        section.className = 'node-stats-chart-section';

        const title = document.createElement('div');
        title.className = 'node-stats-chart-title';
        title.textContent = t('Movimientos Financieros');
        section.appendChild(title);

        const wrapper = document.createElement('div');
        wrapper.className = 'node-stats-chart-wrapper';
        wrapper.style.height = '220px';
        const canvas = document.createElement('canvas');
        wrapper.appendChild(canvas);
        section.appendChild(wrapper);
        container.appendChild(section);

        this._addChart(
            canvas.getContext('2d'),
            'bar',
            labels,
            [
                {
                    label: t('Depósitos'),
                    data: depositosData,
                    backgroundColor: this.CHART_COLOR_DEPOSITOS + '99',
                    borderColor: this.CHART_COLOR_DEPOSITOS,
                    borderWidth: 1,
                },
                {
                    label: t('Retiros'),
                    data: retirosData,
                    backgroundColor: this.CHART_COLOR_RETIROS + '99',
                    borderColor: this.CHART_COLOR_RETIROS,
                    borderWidth: 1,
                },
            ],
            this._buildDarkThemeMoneyChartOptions()
        );
    },

    _renderMarginChart(container, labels, margenData) {
        const section = document.createElement('div');
        section.className = 'node-stats-chart-section';

        const title = document.createElement('div');
        title.className = 'node-stats-chart-title';
        title.textContent = t('Hold % (GGR / Jugado)');
        section.appendChild(title);

        const wrapper = document.createElement('div');
        wrapper.className = 'node-stats-chart-wrapper';
        wrapper.style.height = '200px';
        const canvas = document.createElement('canvas');
        wrapper.appendChild(canvas);
        section.appendChild(wrapper);
        container.appendChild(section);

        this._addChart(
            canvas.getContext('2d'),
            'line',
            labels,
            [
                {
                    label: t('Margen %'),
                    data: margenData,
                    borderColor: this.CHART_COLOR_MARGEN,
                    backgroundColor: this.CHART_COLOR_MARGEN + '22',
                    fill: true,
                    tension: 0.3,
                    pointRadius: 2,
                    borderWidth: 2,
                },
            ],
            this._buildDarkThemePercentageChartOptions()
        );
    },

    // ═══════════════════════════════════════════════════════════════════════
    // CHART HELPERS — _addChart / _destroyCharts / _createChartSlot.
    // Mismo patrón que LotowheelSimulator (consistencia entre dashboards).
    // ═══════════════════════════════════════════════════════════════════════

    // 6º arg opcional localPlugins[]: plugins inline por-chart (gradiente per-barra,
    // crosshair, donut-center, valor-en-punta, etc. — ver charts-premium-helpers.js).
    // Los 4 call-sites previos no lo pasan → llega undefined → [] (sin cambio).
    _addChart(ctx, type, labels, datasets, opts, localPlugins) {
        const chart = new Chart(ctx, {
            type: type,
            data: { labels: labels, datasets: datasets },
            options: opts,
            plugins: localPlugins || [],
        });
        this._activeCharts.push(chart);
        return chart;
    },

    _destroyCharts() {
        for (let i = 0; i < this._activeCharts.length; i++) {
            this._activeCharts[i].destroy();
        }
        this._activeCharts = [];
        const container = document.getElementById('nodeStatsDashboardChartsContainer');
        if (container) container.innerHTML = '';
    },

    _buildDarkThemeMoneyChartOptions() {
        return {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: {
                    labels: {
                        color: '#ccc',
                        font: { size: 10 },
                        usePointStyle: true,
                        pointStyle: 'line',
                    },
                },
                tooltip: {
                    backgroundColor: 'rgba(0,0,0,0.85)',
                    callbacks: {
                        label: function (ctx) {
                            const F = MonedaService.getUnidadesMenoresPorUnidad(MonedaService.getContexto());
                            return ctx.dataset.label + ': ' + MonedaService.format(Math.round(ctx.parsed.y * F));
                        },
                    },
                },
            },
            scales: {
                x: {
                    ticks: { color: '#888', maxTicksLimit: 15, font: { size: 10 } },
                    grid: { color: 'rgba(255,255,255,0.05)' },
                },
                y: {
                    ticks: {
                        color: '#aaa',
                        font: { size: 10 },
                        callback: function (v) {
                            const F = MonedaService.getUnidadesMenoresPorUnidad(MonedaService.getContexto());
                            return MonedaService.format(Math.round(v * F));
                        },
                    },
                    grid: { color: 'rgba(255,255,255,0.05)' },
                },
            },
        };
    },

    _buildDarkThemePercentageChartOptions() {
        return {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: {
                    labels: {
                        color: '#ccc',
                        font: { size: 10 },
                        usePointStyle: true,
                        pointStyle: 'line',
                    },
                },
                tooltip: {
                    backgroundColor: 'rgba(0,0,0,0.85)',
                    callbacks: {
                        label: function (ctx) {
                            return ctx.dataset.label + ': ' + ctx.parsed.y.toFixed(1) + '%';
                        },
                    },
                },
            },
            scales: {
                x: {
                    ticks: { color: '#888', maxTicksLimit: 15, font: { size: 10 } },
                    grid: { color: 'rgba(255,255,255,0.05)' },
                },
                y: {
                    ticks: {
                        color: '#aaa',
                        font: { size: 10 },
                        callback: function (v) {
                            return v.toFixed(0) + '%';
                        },
                    },
                    grid: { color: 'rgba(255,255,255,0.05)' },
                },
            },
        };
    },

    _updateRangeLabel() {
        const el = document.getElementById('nodeStatsRangeLabel');
        if (!el) return;
        const desde = this._rangoDesde;
        const hasta = this._rangoHasta;
        if (!desde || !hasta) {
            el.textContent = '';
            return;
        }
        el.textContent = this._formatHumanDate(desde) + '  →  ' + this._formatHumanDate(hasta);
    },

    _formatHumanDate(isoDate) {
        // "2026-02-23" → "23 feb 2026"
        const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
        const parts = isoDate.split('-');
        if (parts.length !== 3) return isoDate;
        return parseInt(parts[2]) + ' ' + meses[parseInt(parts[1]) - 1] + ' ' + parts[0];
    },

    _formatChartDateLabel(fecha) {
        // Entrada: "2026-02-25" o "2026-W09" → salida legible
        if (fecha.includes('W')) return fecha; // Formato semanal, mostrar tal cual
        // Extraer día/mes
        const parts = fecha.split('-');
        if (parts.length === 3) return parts[2] + '/' + parts[1];
        return fecha;
    },

    // ═══════════════════════════════════════════════════════════════════════
    // CONTRATO Y OBLIGACIONES — panel inline con info contractual del nodo.
    // ═══════════════════════════════════════════════════════════════════════

    _renderContractInfo() {
        const container = document.getElementById('nodeStatsDashboardContractInfo');
        const contrato = this._lastContratoResponse;
        const obligaciones = this._lastObligacionesResponse;

        if (!contrato && (!obligaciones || obligaciones.length === 0)) {
            container.innerHTML = '';
            return;
        }

        let html = '<div class="node-stats-contract-columns">';

        // Columna 1: Contrato
        if (contrato) {
            const baseLabel = contrato.comision_base === 'recarga_jugada' ? 'sobre recarga jugada' : 'sobre GGR';
            const politicaLabel = this._getNetwinNegativoPoliticaLabel(contrato.netwin_negativo_politica);
            const heredadoInfo = contrato.heredado_de ? ' (heredado de ' + contrato.heredado_de.nombre + ')' : '';

            html +=
                '<div class="node-stats-contract-section">' +
                '<h4>' +
                t('Contrato') +
                heredadoInfo +
                '</h4>' +
                this._buildContractRow(t('Comisión'), contrato.comision_porcentaje + '% ' + baseLabel) +
                this._buildContractRow(t('Pérdidas'), politicaLabel) +
                (contrato.fondo_maniobra_limite
                    ? this._buildContractRow(t('Fondo maniobra'), App.formatMoney(contrato.fondo_maniobra_limite))
                    : '') +
                '</div>';
        }

        // Columna 2: Obligaciones
        if (obligaciones && obligaciones.length > 0) {
            html += '<div class="node-stats-contract-section"><h4>' + t('Obligaciones') + '</h4>';
            for (let i = 0; i < obligaciones.length; i++) {
                const ob = obligaciones[i];
                const baseLabel = ob.base === 'venta' ? 'ventas' : ob.base;
                const heredadaTag = ob.es_heredada ? ' (heredada)' : '';
                html += this._buildContractRow(ob.nombre + heredadaTag, ob.porcentaje + '% sobre ' + baseLabel);
            }
            html += '</div>';
        }

        html += '</div>';
        container.innerHTML = html;
    },

    _buildContractRow(label, value) {
        return (
            '<div class="node-stats-contract-row">' +
            '<span class="node-stats-contract-label">' +
            label +
            '</span>' +
            '<span class="node-stats-contract-value">' +
            value +
            '</span>' +
            '</div>'
        );
    },

    _getNetwinNegativoPoliticaLabel(politica) {
        const labels = {
            absorbe: t('Absorbe pérdidas'),
            traslada: t('Traslada pérdidas'),
            compartido: t('Compartido'),
        };
        return labels[politica] || politica || t('No definido');
    },

    // ═══════════════════════════════════════════════════════════════════════
    // HISTORIAL DE CORTES — tabla colapsable de cortes recientes del nodo.
    // ═══════════════════════════════════════════════════════════════════════

    _renderCortesHistory(stats) {
        const container = document.getElementById('nodeStatsDashboardCortesHistory');

        if (!stats.ultimo_corte) {
            container.innerHTML = '';
            return;
        }

        const corte = stats.ultimo_corte;
        const html =
            '<div style="margin-top:8px;">' +
            '<button class="node-stats-cortes-toggle" data-action="nodeStats:toggleCortesSection">' +
            '<span class="chevron" id="nodeStatsCortesChevron">&#9654;</span>' +
            t(' Último Corte: ') +
            corte.fecha +
            ' — GGR: ' +
            App.formatMoney(corte.ggr || 0) +
            '</button>' +
            '<div class="node-stats-cortes-container" id="nodeStatsCortesContainer">' +
            '<div class="node-stats-contract-section" style="margin-top:8px;">' +
            this._buildContractRow(t('ID Corte'), '#' + corte.id) +
            this._buildContractRow(t('Fecha'), corte.fecha) +
            this._buildContractRow('GGR', App.formatMoney(corte.ggr || 0)) + // GGR: acrónimo no se traduce
            '</div>' +
            '</div>' +
            '</div>';

        container.innerHTML = html;
    },

    // Cambia la pestaña activa (Nodo / Jugadores / Tickets). Solo togglea la
    // visibilidad de los paneles — los datos ya están cargados (Promise.all).
    setTab(tabId) {
        const modal = document.getElementById('modalNodeStatsDashboard');
        if (!modal) return;
        modal.querySelectorAll('.node-stats-tab').forEach(t => {
            t.classList.toggle('active', t.dataset.tab === tabId);
        });
        modal.querySelectorAll('.node-stats-panel').forEach(p => {
            p.style.display = p.dataset.panel === tabId ? 'block' : 'none';
        });
        // El tab Tickets carga bajo demanda la primera vez (lazy).
        if (tabId === 'tickets' && !this._ticketsCargados) {
            this._renderTickets(this._lastTicketsResponse);
            this._ticketsCargados = true;
        }
    },

    // Bloque de analytics de jugadores: KPIs + ranking + jugadas por hora.
    // Todo de /api/admin/nodos/:id/jugadores (tabla movimientos, lectura).
    _renderJugadores(resp) {
        const container = document.getElementById('nodeStatsDashboardJugadores');
        if (!container) return;
        const resumen = resp?.resumen;
        const ranking = resp?.ranking || [];
        const porHora = resp?.porHora || [];

        if (!resumen || resumen.total_apuestas === 0) {
            container.innerHTML =
                '<div class="node-stats-section-title" style="margin-top:16px;">' +
                t('Jugadores') +
                '</div>' +
                '<div class="node-stats-no-data">' +
                t('Sin apuestas en el período') +
                '</div>';
            return;
        }

        const ESC = escapeHtmlParaInyeccionSegura;
        const apuestasPorJugador =
            resumen.jugadores_unicos > 0 ? Math.round(resumen.total_apuestas / resumen.jugadores_unicos) : 0;

        // KPIs de jugadores
        const kpis =
            '<div class="node-stats-kpi-grid" style="margin-top:8px;">' +
            this._buildKpiCard(t('Jugadores únicos'), String(resumen.jugadores_unicos), 'cyan') +
            this._buildKpiCard(t('Apuestas'), String(resumen.total_apuestas), 'blue') +
            this._buildKpiCard(t('Apuesta media'), App.formatMoney(resumen.apuesta_media), 'green') +
            this._buildKpiCard(t('Apuestas/jugador'), String(apuestasPorJugador), 'green') +
            this._buildKpiCard(t('Apuesta máx.'), App.formatMoney(resumen.apuesta_max), 'orange') +
            '</div>';

        // Ranking top jugadores (clickeable → ficha). Neto = ganó − apostó (>0 el
        // jugador va ganando = la casa pierde con él, en rojo). Win% = premios/apuestas.
        const filasRanking = ranking
            .map((j, i) => {
                const tel = j.telefono ? ESC(j.telefono) : 'Cliente #' + j.cliente_id;
                const neto = (j.premios_volumen || 0) - (j.volumen || 0);
                const netoClass = neto > 0 ? 'text-danger' : 'text-success'; // jugador gana = casa pierde
                const winPct = j.apuestas > 0 ? Math.round(((j.premios_count || 0) / j.apuestas) * 100) : 0;
                const alta = j.alta ? this._formatChartDateLabel(j.alta.split(' ')[0]) : '-';
                return (
                    '<tr class="node-stats-ranking-row" data-action="nodeStats:verFichaJugador" data-cliente="' +
                    j.cliente_id +
                    '">' +
                    '<td>' +
                    (i + 1) +
                    '</td>' +
                    '<td>' +
                    tel +
                    '</td>' +
                    '<td style="text-align:right">' +
                    j.apuestas +
                    '</td>' +
                    '<td style="text-align:right">' +
                    App.formatMoney(j.volumen) +
                    '</td>' +
                    '<td style="text-align:right" class="' +
                    netoClass +
                    '">' +
                    App.formatMoney(neto) +
                    '</td>' +
                    '<td style="text-align:right">' +
                    winPct +
                    '%</td>' +
                    '<td style="text-align:right">' +
                    alta +
                    '</td>' +
                    '</tr>'
                );
            })
            .join('');
        const tablaRanking =
            '<div class="node-stats-section-subtitle" style="margin-top:12px;">' +
            t('Top jugadores (clic para ver ficha)') +
            '</div>' +
            '<table class="node-stats-ranking-table"><thead><tr>' +
            '<th>#</th><th>' +
            t('Jugador') +
            '</th><th style="text-align:right">' +
            t('Apuestas') +
            '</th>' +
            '<th style="text-align:right">' +
            t('Volumen') +
            '</th><th style="text-align:right">' +
            t('Neto') +
            '</th>' +
            '<th style="text-align:right">Win%</th><th style="text-align:right">' +
            t('Alta') +
            '</th>' +
            '</tr></thead><tbody>' +
            filasRanking +
            '</tbody></table>';

        container.innerHTML =
            '<div class="node-stats-section-title" style="margin-top:16px;">' +
            t('Jugadores') +
            '</div>' +
            kpis +
            tablaRanking +
            '<div class="node-stats-section-subtitle" style="margin-top:12px;">' +
            t('Jugadas por hora del día') +
            '</div>' +
            '<div style="position:relative;height:180px;"><canvas id="nodeStatsJugadoresPorHoraChart"></canvas></div>';

        // Gráfico de barras: apuestas por hora (0-23). Relleno horas sin datos con 0.
        this._renderPorHoraChart(porHora);
    },

    _renderPorHoraChart(porHora) {
        const canvas = document.getElementById('nodeStatsJugadoresPorHoraChart');
        if (!canvas) return;
        if (this._chartPorHora) this._chartPorHora.destroy();
        // Spine completa 0-23 (horas sin apuestas = 0) para un eje legible.
        const apuestasPorHora = new Array(24).fill(0);
        for (let i = 0; i < porHora.length; i++) {
            const h = porHora[i].hora;
            if (h >= 0 && h <= 23) apuestasPorHora[h] = porHora[i].apuestas;
        }
        const labels = [];
        for (let h = 0; h < 24; h++) labels.push(String(h).padStart(2, '0') + 'h');
        this._chartPorHora = new Chart(canvas.getContext('2d'), {
            type: 'bar',
            data: {
                labels,
                datasets: [
                    {
                        label: t('Apuestas'),
                        data: apuestasPorHora,
                        backgroundColor: this.CHART_COLOR_VENTAS || '#22c55e',
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
            },
        });
    },

    // Tab Tickets: analytics de bearer_tickets (caja física). Vacío para operadores
    // 100% wallet (sin tickets). De /api/admin/nodos/:id/tickets.
    _renderTickets(resp) {
        const container = document.getElementById('nodeStatsDashboardTickets');
        if (!container) return;
        const r = resp?.resumen;
        const sorteos = resp?.sorteos || [];

        if (!r || r.total_tickets === 0) {
            container.innerHTML =
                '<div class="node-stats-no-data" style="margin-top:16px;">' +
                t('Este nodo no vendió tickets en el período (opera por wallet/online).') +
                '</div>';
            return;
        }

        const ESC = escapeHtmlParaInyeccionSegura;
        const kpis =
            '<div class="node-stats-kpi-grid" style="margin-top:8px;">' +
            this._buildKpiCard(t('Tickets'), String(r.total_tickets), 'blue') +
            this._buildKpiCard(t('Vendido'), App.formatMoney(r.vendido), 'green') +
            this._buildKpiCard(t('Premios pagados'), App.formatMoney(r.premios_pagados), 'orange') +
            this._buildKpiCard(t('Ganadores'), String(r.ganadores), 'cyan') +
            this._buildKpiCard(t('Cobrados'), String(r.cobrados), 'green') +
            this._buildKpiCard(t('Anulados'), String(r.anulados), 'red') +
            '</div>';

        const filasSorteos = sorteos
            .map(
                s =>
                    '<tr><td>' +
                    t('Sorteo #') +
                    ESC(String(s.sorteo_config_id)) +
                    '</td><td style="text-align:right">' +
                    s.tickets +
                    '</td><td style="text-align:right">' +
                    App.formatMoney(s.vendido) +
                    '</td></tr>'
            )
            .join('');
        const tabla = sorteos.length
            ? '<div class="node-stats-section-subtitle" style="margin-top:12px;">' +
              t('Sorteos más vendidos') +
              '</div>' +
              '<table class="node-stats-ranking-table"><thead><tr><th>' +
              t('Sorteo') +
              '</th>' +
              '<th style="text-align:right">' +
              t('Tickets') +
              '</th><th style="text-align:right">' +
              t('Vendido') +
              '</th>' +
              '</tr></thead><tbody>' +
              filasSorteos +
              '</tbody></table>'
            : '';

        container.innerHTML =
            '<div class="node-stats-section-title" style="margin-top:16px;">' + t('Tickets') + '</div>' + kpis + tabla;
    },

    // Ficha de UN jugador (click en el ranking). Overlay sobre el modal de stats.
    // De /api/admin/nodos/:id/jugadores/:clienteId.
    async verFichaJugador(clienteId) {
        try {
            const resp = await App.fetchWithAuth('/api/admin/nodos/' + this._nodoId + '/jugadores/' + clienteId);
            if (!resp || !resp.cliente) {
                App.toast(t('No se pudo cargar el jugador'), 'error');
                return;
            }
            const ESC = escapeHtmlParaInyeccionSegura;
            const c = resp.cliente;
            const t = resp.totales || {};
            const sorteos = resp.sorteos || [];
            const apostado = t.apostado || 0;
            const ganado = t.ganado || 0;
            const neto = ganado - apostado;
            const netoClass = neto > 0 ? 'text-danger' : 'text-success';
            const winPct = t.apuestas > 0 ? Math.round(((t.premios_count || 0) / t.apuestas) * 100) : 0;
            const nombre = c.nombre ? ESC(c.nombre) : ESC(c.telefono || 'Cliente #' + c.id);
            const alta = c.created_at ? this._formatChartDateLabel(c.created_at.split(' ')[0]) : '-';

            const filasSorteos = sorteos
                .map(
                    s =>
                        '<tr><td>' +
                        ESC(s.sorteo || '-') +
                        '</td><td style="text-align:right">' +
                        s.apuestas +
                        '</td><td style="text-align:right">' +
                        App.formatMoney(s.volumen) +
                        '</td></tr>'
                )
                .join('');

            let panel = document.getElementById('nodeStatsFichaJugadorPanel');
            if (!panel) {
                panel = document.createElement('div');
                panel.id = 'nodeStatsFichaJugadorPanel';
                panel.className = 'node-stats-ficha-jugador-overlay';
                document.body.appendChild(panel);
            }
            panel.innerHTML =
                '<div class="node-stats-ficha-jugador">' +
                '<div class="node-stats-ficha-jugador-header">' +
                '<h3>' +
                nombre +
                '</h3>' +
                '<button class="btn-icon" data-action="nodeStats:cerrarFichaJugador">✕</button>' +
                '</div>' +
                '<dl class="node-stats-ficha-jugador-dl">' +
                '<dt>' +
                t('Teléfono') +
                '</dt><dd>' +
                ESC(c.telefono || '-') +
                '</dd>' +
                '<dt>' +
                t('Alta') +
                '</dt><dd>' +
                alta +
                '</dd>' +
                '<dt>' +
                t('Días activos') +
                '</dt><dd>' +
                (t.dias_activos || 0) +
                '</dd>' +
                '<dt>' +
                t('Apuestas') +
                '</dt><dd>' +
                (t.apuestas || 0) +
                '</dd>' +
                '<dt>' +
                t('Jugado') +
                '</dt><dd>' +
                App.formatMoney(apostado) +
                '</dd>' +
                '<dt>' +
                t('Ganado') +
                '</dt><dd>' +
                App.formatMoney(ganado) +
                '</dd>' +
                '<dt>' +
                t('Neto') +
                '</dt><dd class="' +
                netoClass +
                '">' +
                App.formatMoney(neto) +
                '</dd>' +
                '<dt>Win%</dt><dd>' +
                winPct +
                '%</dd>' +
                '</dl>' +
                (sorteos.length
                    ? '<div class="node-stats-section-subtitle" style="margin-top:10px;">' +
                      t('Sorteos apostados') +
                      '</div>' +
                      '<table class="node-stats-ranking-table"><thead><tr><th>' +
                      t('Sorteo') +
                      '</th>' +
                      '<th style="text-align:right">' +
                      t('Apuestas') +
                      '</th><th style="text-align:right">' +
                      t('Volumen') +
                      '</th>' +
                      '</tr></thead><tbody>' +
                      filasSorteos +
                      '</tbody></table>'
                    : '') +
                '</div>';
            panel.classList.add('abierto');
        } catch (e) {
            console.error('[NodeStats] Error ficha jugador:', e);
            App.toast(t('Error al cargar el jugador'), 'error');
        }
    },

    cerrarFichaJugador() {
        const panel = document.getElementById('nodeStatsFichaJugadorPanel');
        if (panel) panel.classList.remove('abierto');
    },

    _toggleCortesSection() {
        const container = document.getElementById('nodeStatsCortesContainer');
        const chevron = document.getElementById('nodeStatsCortesChevron');
        if (!container) return;

        const isExpanded = container.classList.contains('expanded');
        if (isExpanded) {
            container.classList.remove('expanded');
            chevron.classList.remove('expanded');
        } else {
            container.classList.add('expanded');
            chevron.classList.add('expanded');
        }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // EXPORTACIÓN — texto plano, imagen PNG (canvas), compartir WhatsApp.
    // ═══════════════════════════════════════════════════════════════════════

    async copyStatsAsPlainText() {
        const text = this._formatStatsAsPlainText();
        if (!text) {
            App.toast(t('No hay datos para copiar'), 'info');
            return;
        }

        try {
            await navigator.clipboard.writeText(text);
            App.toast(t('Copiado al portapapeles'));
        } catch (e) {
            // Fallback para navegadores sin soporte de Clipboard API
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            App.toast(t('Copiado al portapapeles'));
        }
    },

    async downloadChartsAsImage() {
        const stats = this._lastStatsResponse;
        if (!stats) {
            App.toast(t('No hay datos para exportar'), 'info');
            return;
        }

        // Dimensiones del canvas compuesto
        const CANVAS_WIDTH = 1200;
        const PADDING = 32;
        const KPI_HEIGHT = 70;
        const KPI_GAP = 12;
        const CHART_HEIGHT = 260;
        const SECTION_GAP = 24;
        const CONTRACT_ROW_HEIGHT = 28;

        // Calcular altura total
        let totalHeight = PADDING; // top padding
        totalHeight += 40; // título
        totalHeight += 32; // rango de fechas (bloque visible)
        totalHeight += SECTION_GAP;
        totalHeight += KPI_HEIGHT; // KPIs (una fila)
        totalHeight += SECTION_GAP;

        const chartsCount = this._activeCharts.length;
        if (chartsCount > 0) {
            totalHeight += chartsCount * (CHART_HEIGHT + SECTION_GAP);
        }

        // Contrato
        const contrato = this._lastContratoResponse;
        let contratoRowCount = 0;
        if (contrato) {
            contratoRowCount = 2; // comision + perdidas
            if (contrato.fondo_maniobra_limite) contratoRowCount++;
            totalHeight += 30 + contratoRowCount * CONTRACT_ROW_HEIGHT + SECTION_GAP;
        }

        totalHeight += PADDING; // bottom padding

        // Crear canvas
        const canvas = document.createElement('canvas');
        canvas.width = CANVAS_WIDTH;
        canvas.height = totalHeight;
        const ctx = canvas.getContext('2d');

        // Fondo dark
        ctx.fillStyle = '#0f1729';
        ctx.fillRect(0, 0, CANVAS_WIDTH, totalHeight);

        let cursorY = PADDING;

        // --- Título ---
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 22px -apple-system, BlinkMacSystemFont, sans-serif';
        ctx.fillText(t('Estadísticas: ') + (this._nodoNombre || 'Nodo'), PADDING, cursorY + 28);
        cursorY += 40;

        // --- Rango de fechas (bloque visible, fondo claro) ---
        const desdeHuman = this._rangoDesde ? this._formatHumanDate(this._rangoDesde) : '';
        const hastaHuman = this._rangoHasta ? this._formatHumanDate(this._rangoHasta) : '';
        const rangoTexto = desdeHuman + '  →  ' + hastaHuman;
        ctx.font = 'bold 15px -apple-system, BlinkMacSystemFont, sans-serif';
        const rangoTextWidth = ctx.measureText(rangoTexto).width;
        const rangoPadH = 20;
        const rangoPadV = 8;
        const rangoBoxWidth = rangoTextWidth + rangoPadH * 2;
        const rangoBoxHeight = 32;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.88)';
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
        ctx.lineWidth = 1;
        this._roundRect(ctx, PADDING, cursorY, rangoBoxWidth, rangoBoxHeight, 6);
        ctx.fillStyle = '#1a1a2e';
        ctx.font = 'bold 15px -apple-system, BlinkMacSystemFont, sans-serif';
        ctx.fillText(rangoTexto, PADDING + rangoPadH, cursorY + rangoPadV + 16);
        cursorY += rangoBoxHeight + SECTION_GAP;

        // --- KPIs ---
        const produccion = stats.produccion || {};
        const financiero = stats.financiero || {};
        // La imagen exportada SALE del sistema (se comparte por WhatsApp sin contexto),
        // así que es donde un cero falso hace más daño: nadie puede contrastarlo. Los
        // campos sin consolidar se pintan '—' en gris, nunca un número inventado.
        const SIN_DATO = '—';
        const GRIS_SIN_DATO = '#9ca3af';
        const imgVentas = produccion.jugado_total;
        const imgPremios = produccion.pagado_total;
        const imgNetwin = produccion.ggr;
        const hayRatiosImg = imgVentas !== null && imgPremios !== null && imgVentas > 0;
        const imgRtp = hayRatiosImg ? ((imgPremios / imgVentas) * 100).toFixed(1) : null;
        const imgHold = hayRatiosImg ? (((imgVentas - imgPremios) / imgVentas) * 100).toFixed(1) : null;
        // Prefijo ≈ si el total viene de una conversión por tasa (es aproximado).
        // ≈ POR BLOQUE: producción y financiero se consolidan con queries distintas,
        // así que una puede venir convertida por tasa y la otra ser exacta.
        const dineroConFlags = flags => valor =>
            valor === null || valor === undefined
                ? SIN_DATO
                : (flags && flags.consolidado_aproximado ? '≈ ' : '') + App.formatMoney(valor);
        const dineroImg = dineroConFlags(stats.moneda_meta_produccion || stats.moneda_meta);
        const dineroImgFinanciero = dineroConFlags(stats.moneda_meta);
        const colorDinero = (valor, colorNormal) =>
            valor === null || valor === undefined ? GRIS_SIN_DATO : colorNormal;
        const kpis = [
            { label: t('Jugado').toUpperCase(), value: dineroImg(imgVentas), color: colorDinero(imgVentas, '#22c55e') },
            {
                label: t('Ganado').toUpperCase(),
                value: dineroImg(imgPremios),
                color: colorDinero(imgPremios, '#3b82f6'),
            },
            {
                label: 'NETWIN',
                value: dineroImg(imgNetwin),
                // El signo se evalúa SOLO si hay cifra: `null >= 0` es true en JS, así
                // que un ternario suelto calcularía "verde" para un valor inexistente.
                color:
                    imgNetwin === null || imgNetwin === undefined
                        ? GRIS_SIN_DATO
                        : imgNetwin >= 0
                          ? '#22c55e'
                          : '#ef4444',
            },
            {
                label: 'RTP',
                value: imgRtp === null ? SIN_DATO : imgRtp + '%',
                color: imgRtp === null ? GRIS_SIN_DATO : parseFloat(imgRtp) > 100 ? '#ef4444' : '#22c55e',
            },
            {
                label: 'HOLD',
                value: imgHold === null ? SIN_DATO : imgHold + '%',
                color: imgHold === null ? GRIS_SIN_DATO : parseFloat(imgHold) >= 0 ? '#22c55e' : '#ef4444',
            },
            {
                label: t('Depósitos').toUpperCase(),
                value: dineroImgFinanciero(financiero.recarga_dinero_fresco),
                color: colorDinero(financiero.recarga_dinero_fresco, '#06b6d4'),
            },
            {
                label: t('Retiros').toUpperCase(),
                value: dineroImgFinanciero(financiero.retiro_dinero_real),
                color: colorDinero(financiero.retiro_dinero_real, '#f97316'),
            },
            {
                label: t('Saldo CC').toUpperCase(),
                value: App.formatMoney(stats.saldo_cuenta_corriente || 0),
                color: '#a855f7',
            },
        ];

        const kpiCardWidth = (CANVAS_WIDTH - 2 * PADDING - (kpis.length - 1) * KPI_GAP) / kpis.length;
        for (let i = 0; i < kpis.length; i++) {
            const kpi = kpis[i];
            const x = PADDING + i * (kpiCardWidth + KPI_GAP);

            // Card background
            ctx.fillStyle = 'rgba(255,255,255,0.05)';
            ctx.strokeStyle = 'rgba(255,255,255,0.1)';
            ctx.lineWidth = 1;
            this._roundRect(ctx, x, cursorY, kpiCardWidth, KPI_HEIGHT, 6);

            // Label
            ctx.fillStyle = '#8899aa';
            ctx.font = '10px -apple-system, BlinkMacSystemFont, sans-serif';
            ctx.fillText(kpi.label, x + 10, cursorY + 20);

            // Value
            ctx.fillStyle = kpi.color;
            ctx.font = 'bold 18px -apple-system, BlinkMacSystemFont, sans-serif';
            ctx.fillText(kpi.value, x + 10, cursorY + 50);
        }
        cursorY += KPI_HEIGHT + SECTION_GAP;

        // --- Charts (renderizar cada Chart.js a imagen) ---
        const chartTitles = [t('Evolución de Producción'), t('Movimientos Financieros'), t('Margen %')];
        for (let i = 0; i < chartsCount; i++) {
            const chart = this._activeCharts[i];
            const chartTitle = chartTitles[i] || '';

            // Título del gráfico
            ctx.fillStyle = '#cccccc';
            ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, sans-serif';
            ctx.textTransform = 'uppercase';
            ctx.fillText(chartTitle.toUpperCase(), PADDING, cursorY + 14);
            cursorY += 22;

            // Renderizar chart como imagen
            const chartImg = new Image();
            const chartDataUrl = chart.toBase64Image('image/png', 2);
            await new Promise(resolve => {
                chartImg.onload = resolve;
                chartImg.src = chartDataUrl;
            });
            const chartDrawWidth = CANVAS_WIDTH - 2 * PADDING;
            ctx.drawImage(chartImg, PADDING, cursorY, chartDrawWidth, CHART_HEIGHT - 22);
            cursorY += CHART_HEIGHT - 22 + SECTION_GAP;
        }

        // --- Contrato ---
        if (contrato) {
            ctx.fillStyle = '#cccccc';
            ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, sans-serif';
            ctx.fillText(t('Contrato').toUpperCase(), PADDING, cursorY + 14);
            cursorY += 28;

            // Card background
            const contractCardHeight = contratoRowCount * CONTRACT_ROW_HEIGHT + 12;
            ctx.fillStyle = 'rgba(255,255,255,0.03)';
            ctx.strokeStyle = 'rgba(255,255,255,0.08)';
            this._roundRect(ctx, PADDING, cursorY, CANVAS_WIDTH - 2 * PADDING, contractCardHeight, 6);
            cursorY += 8;

            const baseLabel = contrato.comision_base === 'recarga_jugada' ? 'sobre recarga jugada' : 'sobre GGR';
            const contratoRows = [
                { label: t('Comisión'), value: contrato.comision_porcentaje + '% ' + baseLabel },
                {
                    label: t('Pérdidas'),
                    value: this._getNetwinNegativoPoliticaLabel(contrato.netwin_negativo_politica),
                },
            ];
            if (contrato.fondo_maniobra_limite) {
                contratoRows.push({
                    label: t('Fondo maniobra'),
                    value: App.formatMoney(contrato.fondo_maniobra_limite),
                });
            }

            for (let i = 0; i < contratoRows.length; i++) {
                const row = contratoRows[i];
                ctx.fillStyle = '#8899aa';
                ctx.font = '13px -apple-system, BlinkMacSystemFont, sans-serif';
                ctx.fillText(row.label, PADDING + 16, cursorY + 18);
                ctx.fillStyle = '#ffffff';
                ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, sans-serif';
                ctx.fillText(row.value, CANVAS_WIDTH / 2, cursorY + 18);
                cursorY += CONTRACT_ROW_HEIGHT;
            }
        }

        // Descargar
        const downloadLink = document.createElement('a');
        downloadLink.href = canvas.toDataURL('image/png', 1);
        downloadLink.download =
            'stats-' +
            (this._nodoNombre || 'nodo').replace(/\s+/g, '-') +
            '-' +
            (this._rangoDesde || 'periodo') +
            '.png';
        downloadLink.click();

        App.toast(t('Imagen descargada'));
    },

    /**
     * Dibuja un rectángulo redondeado con fill + stroke
     */
    _roundRect(ctx, x, y, width, height, radius) {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
    },

    async shareViaWhatsApp() {
        const text = this._formatStatsAsPlainText();
        if (!text) {
            App.toast(t('No hay datos para compartir'), 'info');
            return;
        }

        const encodedText = encodeURIComponent(text);
        window.open('https://wa.me/?text=' + encodedText, '_blank', 'noopener,noreferrer');
    },

    _formatStatsAsPlainText() {
        const stats = this._lastStatsResponse;
        if (!stats) return '';

        const desde = this._rangoDesde || 'inicio';
        const hasta = this._rangoHasta || 'actual';
        const tipoLabel = this._getTipoLabel(stats.nodo?.tipo);

        // Este texto se comparte por WhatsApp (ver _compartirPorWhatsApp): sale del
        // sistema igual que la imagen, así que un cero falso acá es igual de grave —
        // el receptor no tiene forma de contrastarlo. Mismo criterio: sin consolidado
        // se escribe '—', nunca una cifra.
        const SIN_DATO_TEXTO = '—';
        // ≈ por bloque, igual que en pantalla y en la imagen.
        const dineroTextoCon = flags => valor =>
            valor === null || valor === undefined
                ? SIN_DATO_TEXTO
                : (flags && flags.consolidado_aproximado ? '≈ ' : '') + App.formatMoney(valor);
        const dineroTexto = dineroTextoCon(stats.moneda_meta_produccion || stats.moneda_meta);
        const dineroTextoFinanciero = dineroTextoCon(stats.moneda_meta);

        const lines = [
            stats.nodo?.nombre || this._nodoNombre || 'Nodo',
            t('Tipo: ') + tipoLabel,
            t('Periodo: ') + desde + t(' a ') + hasta,
            '',
            t('--- PRODUCCIÓN ---'),
            t('  Jugado: ') + dineroTexto(stats.produccion?.jugado_total),
            t('  Ganado: ') + dineroTexto(stats.produccion?.pagado_total),
            t('  GGR: ') + dineroTexto(stats.produccion?.ggr),
        ];

        const ventas = stats.produccion?.jugado_total;
        const premios = stats.produccion?.pagado_total;
        if (ventas !== null && ventas !== undefined && premios !== null && premios !== undefined && ventas > 0) {
            const rtp = ((premios / ventas) * 100).toFixed(1);
            const hold = (((ventas - premios) / ventas) * 100).toFixed(1);
            lines.push(t('  RTP: ') + rtp + '%');
            lines.push(t('  Hold: ') + hold + '%');
        } else if (ventas === null || premios === null) {
            // Se DECLARA que no se pudo calcular: omitirlos en silencio (como hacía el
            // `ventas > 0`) es indistinguible de un período sin actividad.
            lines.push(t('  RTP: ') + SIN_DATO_TEXTO);
            lines.push(t('  Hold: ') + SIN_DATO_TEXTO);
        }

        lines.push(
            '',
            t('--- FINANCIERO ---'),
            t('  Recargas: ') + dineroTextoFinanciero(stats.financiero?.recarga_dinero_fresco),
            t('  Retiros: ') + dineroTextoFinanciero(stats.financiero?.retiro_dinero_real)
        );

        // Aviso explícito al final: el texto viaja sin la señal visual del ! ámbar.
        const sinTasaEnAlgunBloque =
            (stats.moneda_meta && stats.moneda_meta.tasa_faltante) ||
            (stats.moneda_meta_produccion && stats.moneda_meta_produccion.tasa_faltante);
        if (sinTasaEnAlgunBloque) {
            lines.push('', t('! Red con monedas mixtas y sin tasas de cambio cargadas — total no confiable'));
        }
        // Causa distinta, aviso distinto: no es que falte la tasa, es que hay nodos
        // cuya moneda no se resuelve (PV borrado, o sin moneda en ningún ancestro).
        // Se arregla configurando el nodo, no cargando tasas.
        const hayNodosSinMoneda =
            (stats.moneda_meta && stats.moneda_meta.nodos_sin_moneda) ||
            (stats.moneda_meta_produccion && stats.moneda_meta_produccion.nodos_sin_moneda);
        if (hayNodosSinMoneda) {
            lines.push('', t('! Hay nodos sin moneda configurada — su importe no se pudo convertir'));
        }

        if (stats.saldo_cuenta_corriente) {
            lines.push(
                '',
                t('--- CUENTA CORRIENTE ---'),
                t('  Saldo: ') + App.formatMoney(stats.saldo_cuenta_corriente)
            );
        }

        if (stats.estructura && Object.keys(stats.estructura).length > 0) {
            lines.push('', t('--- ESTRUCTURA ---'));
            const tipos = Object.entries(stats.estructura);
            for (let i = 0; i < tipos.length; i++) {
                lines.push('  ' + this._getTipoLabel(tipos[i][0]) + ': ' + tipos[i][1]);
            }
        }

        // Info de contrato si disponible
        const contrato = this._lastContratoResponse;
        if (contrato) {
            const baseLabel = contrato.comision_base === 'recarga_jugada' ? t('sobre recarga jugada') : t('sobre GGR');
            lines.push(
                '',
                t('--- CONTRATO ---'),
                t('  Comisión: ') + contrato.comision_porcentaje + '% ' + baseLabel,
                t('  Pérdidas: ') + this._getNetwinNegativoPoliticaLabel(contrato.netwin_negativo_politica)
            );
        }

        if (stats.ultimo_corte) {
            lines.push(
                '',
                t('--- ÚLTIMO CORTE ---'),
                t('  Fecha: ') + stats.ultimo_corte.fecha,
                t('  GGR: ') + App.formatMoney(stats.ultimo_corte.ggr || 0)
            );
        }

        return lines.join('\n');
    },

    _getTipoLabel(tipo) {
        const labels = {
            operador: t('Operador'),
            representante: t('Representante'),
            punto_venta: t('Punto de Venta'),
            caja: t('Caja'),
        };
        return labels[tipo] || tipo || '';
    },
};
